import { useEffect, useState } from "react";
import axios from "axios";
import { FlaskConical } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Metric = ({ label, m, testid }) => (
  <div className="rounded-lg border border-[#1A2338] bg-[#0F1A2E] p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest font-num text-slate-400">{label}</div>
    {m ? (
      <div className="font-num text-xs mt-1 space-y-0.5">
        <div>Brier <b className="text-base">{m.brier}</b></div>
        <div className="text-slate-400">Log loss {m.log_loss} · hit rate {m.hit_rate}% · n {m.n}</div>
      </div>
    ) : <div className="text-xs text-slate-500 mt-1">No data</div>}
  </div>
);

const CalTable = ({ model, market }) => {
  const ranges = Array.from({ length: 10 }, (_, i) => `${i * 10}-${(i + 1) * 10}%`);
  const find = (m, r) => (m?.calibration || []).find((b) => b.range === r);
  const pct = (b) => (b ? `${Math.round(b.observed_up * 100)}% (n ${b.n})` : "—");
  return (
    <div className="overflow-x-auto mt-3">
      <Table data-testid="calibration-table">
        <TableHeader>
          <TableRow className="border-[#1A2338] hover:bg-transparent">
            {["Predicted", "Model → actual Up", "Market → actual Up"].map((h) => (
              <TableHead key={h} className="text-[10px] uppercase tracking-wide font-num text-slate-500 px-1">{h}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {ranges.map((r) => (
            <TableRow key={r} className="border-[#1A2338] text-[11px] font-num">
              <TableCell className="px-1">{r}</TableCell>
              <TableCell className="px-1">{pct(find(model, r))}</TableCell>
              <TableCell className="px-1">{pct(find(market, r))}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export const CalibrationCard = () => {
  const [d, setD] = useState(null);
  useEffect(() => {
    const load = () => axios.get(`${API}/analytics/calibration`).then((r) => setD(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 300000);
    return () => clearInterval(id);
  }, []);
  if (!d) return null;
  const s = d.strategy || {};
  return (
    <section className="card p-5 sm:p-6 fade-up" data-testid="calibration-card">
      <h2 className="font-head font-bold text-lg flex items-center gap-2"><FlaskConical className="w-5 h-5 text-[#22D3EE]" /> Backtest & calibration</h2>
      <p className="text-xs text-slate-400 mt-1">
        Replays the last {d.hours || 48}h of Polymarket US 15-min BTC windows ({d.windows || 0} windows, {d.rows || 0} checks at minutes 3/5/8/11/13)
        using only data available at each moment. Brier score: 0 = perfect, 0.25 = coin flip.
      </p>
      <div className={`mt-3 rounded-lg p-3 text-sm ${d.validated ? "bg-emerald-500/10 text-emerald-200" : "bg-amber-500/10 text-amber-200"}`} data-testid="calibration-verdict">
        {d.verdict}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
        <Metric label="Model estimate" m={d.model} testid="calibration-model" />
        <Metric label="Market-implied (order-book mid)" m={d.market} testid="calibration-market" />
        <div className="rounded-lg border border-[#1A2338] bg-[#0F1A2E] p-3" data-testid="calibration-strategy">
          <div className="text-[10px] uppercase tracking-widest font-num text-slate-400">Divergence rule, hold to settlement</div>
          <div className="font-num text-xs mt-1">
            {s.trades ? <>{s.trades} simulated trades · win rate {s.win_rate}% · avg return {s.avg_return_pct}% per trade (after fees)</> : "No trades"}
          </div>
        </div>
      </div>
      {d.held_out && (
        <div className="mt-3 rounded-lg border border-[#1A2338] p-3 text-xs font-num space-y-1" data-testid="calibration-held-out">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Held-out test half ({d.split?.test_rows} checks, later windows)</div>
          <div>Brier — model {d.held_out.model?.brier ?? "—"} · market {d.held_out.market?.brier ?? "—"} · 50% baseline {d.held_out.baseline_50?.brier ?? "—"}</div>
          {d.held_out.brier_diff_ci && <div>Model − market Brier: {d.held_out.brier_diff_ci.mean} (95% CI {d.held_out.brier_diff_ci.low} to {d.held_out.brier_diff_ci.high}; below 0 would favour the model)</div>}
          {d.regimes && Object.keys(d.regimes).length > 0 && <div>By volatility: {Object.entries(d.regimes).map(([k, v]) => `${k.replace("_", " ")} model ${v.model_brier} / market ${v.market_brier} (n ${v.n})`).join(" · ")}</div>}
          {d.missing_data && Object.keys(d.missing_data).length > 0 && <div className="text-slate-500">Skipped: {Object.entries(d.missing_data).map(([k, v]) => `${k.replace(/_/g, " ")} ${v}`).join(" · ")}</div>}
          <div className="text-slate-500">Model {d.model_version} · split: {d.split?.method}</div>
        </div>
      )}
      {d.by_minute && (
        <div className="flex flex-wrap gap-2 mt-3 text-[11px] font-num" data-testid="calibration-by-minute">
          {Object.entries(d.by_minute).map(([m, v]) => (
            <span key={m} className="px-2 py-1 rounded border border-[#1A2338] text-slate-400">min {m}: model {v.model_brier} · market {v.market_brier}</span>
          ))}
        </div>
      )}
      <CalTable model={d.model} market={d.market} />
      {d.generated_at && <p className="text-[10px] text-slate-500 font-num mt-2">Generated {new Date(d.generated_at * 1000).toLocaleString()} · refreshes every 30 min</p>}
    </section>
  );
};
